import React, { useState } from "react";
import { DrawerForm, DrawerFormRef } from "../../../../components/drawers/DrawerForm";
import PaymentMethodPicker, { NEW_CARD } from "./PaymentMethodPicker";
import Icon from "../../../../components/base/Icon";
import CurrentPlan from "../models/CurrentPlan";
import { toShortDateWithoutTime } from "../../../../core/Global";
import "./PlanUpgradeDrawer.css";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })

// Two ways to take an upgrade: pay today and start the new plan now (the billing date moves to
// today), or book it for the renewal date and pay the new price then, with nothing charged
// today. Coming from Free there is no paid period to wait for, so only "now" is offered.
export type UpgradeTiming = "now" | "renewal"

// The second half of an upgrade: what it costs, when it happens, what pays for it, and the press
// that commits it.
//
// Its own drawer over the catalog rather than a section under it. The plan is already chosen by
// the time this opens, so this is the moment to look at once more before money moves -- and the
// drawer underneath staying visible at the edge is what says the choice is still there to go back
// to.
export default function PlanUpgradeConfirmDrawer(props: {
    quote: any,
    plan: CurrentPlan,
    methodId?: string,
    onMethodChange: (id: string) => void,
    loadingMethods: boolean,
    onLoadingMethodsChange: (loading: boolean) => void,
    drawerRef?: React.RefObject<DrawerFormRef>,
    onClose: () => void,
    onPay: (callback: any) => void,
    onSchedule: (callback: any) => void
}) {

    const quote = props.quote
    const period = quote?.billing === "annual" ? "yearly" : "monthly"

    const newCard = props.methodId === NEW_CARD

    const canWait = !props.plan.isFree && !!props.plan.periodEnd
    const renewal = canWait ? toShortDateWithoutTime(props.plan.periodEnd) : ""
    const [timing, timingSet] = useState<UpgradeTiming>("now")
    const atRenewal = canWait && timing === "renewal"

    // A new card is a perfectly good way to pay -- it just happens somewhere else. The button
    // says so, because pressing it leaves the site. An upgrade booked for the renewal date needs
    // no card today: the renewal engine charges the card on file when the date comes.
    const ready = atRenewal || (!!props.methodId && !props.loadingMethods)

    return (
        <DrawerForm id="drawer-plan-upgrade-confirm"
            ref={props.drawerRef}
            title="Confirm your upgrade"
            titleIcon="circle-check"
            subtitle={"Moving to " + (quote?.planName ?? "a new plan")}
            buttonText={
                atRenewal
                    ? "Schedule upgrade for " + renewal
                    : newCard
                        ? "Continue to Stripe"
                        : "Pay " + money.format(quote?.amountDue ?? 0) + " & Upgrade"
            }
            confirmDisabled={!ready}
            onClose={props.onClose}
            onSubmit={(body, callback) => atRenewal ? props.onSchedule(callback) : props.onPay(callback)}>

            <div className="plan-upgrade-head">
                <div className="plan-upgrade-icon">
                    <Icon icon="arrow-up-right-dots" type="solid" size={22} />
                </div>
                <div>
                    <div className="plan-upgrade-name">{quote?.planName}</div>
                    <div className="plan-upgrade-period">Billed {period}</div>
                </div>
            </div>

            {
                canWait &&
                <>
                    <div className="plan-upgrade-section">When</div>
                    <div className="mb-6">
                        <label className="form-check form-check-custom form-check-solid mb-4 align-items-start">
                            <input className="form-check-input mt-1" type="radio" name="upgrade-timing"
                                checked={timing === "now"} onChange={() => timingSet("now")} />
                            <span className="form-check-label ms-3">
                                <span className="fw-bold fs-6 d-block">Upgrade now</span>
                                <span className="fs-7 text-gray-700">
                                    Pay {money.format(quote?.amountDue ?? 0)} today. {quote?.planName} starts right away,
                                    and your billing date moves to today.
                                </span>
                            </span>
                        </label>
                        <label className="form-check form-check-custom form-check-solid align-items-start">
                            <input className="form-check-input mt-1" type="radio" name="upgrade-timing"
                                checked={timing === "renewal"} onChange={() => timingSet("renewal")} />
                            <span className="form-check-label ms-3">
                                <span className="fw-bold fs-6 d-block">Upgrade on {renewal}, my renewal date</span>
                                <span className="fs-7 text-gray-700">
                                    Nothing is charged today. You keep {props.plan.displayName ?? props.plan.planName} until
                                    then; from {renewal} it is {money.format(quote?.price ?? 0)} {period}, on your usual
                                    billing date.
                                </span>
                            </span>
                        </label>
                    </div>
                </>
            }

            {
                atRenewal ?
                    <div className="plan-upgrade-rows">
                        <div className="plan-upgrade-row">
                            <span>{quote?.planName} plan, from {renewal}</span>
                            <span>{money.format(quote?.price ?? 0)} / {period === "yearly" ? "year" : "month"}</span>
                        </div>
                        <div className="plan-upgrade-row is-total">
                            <span>Due today</span>
                            <span>{money.format(0)}</span>
                        </div>
                    </div>
                    :
                    <div className="plan-upgrade-rows">
                        <div className="plan-upgrade-row">
                            <span>{quote?.planName} plan</span>
                            <span>{money.format(quote?.price ?? 0)}</span>
                        </div>
                        {
                            /* Only when there is something to credit. Coming from Free there is not, and a
                               row reading "-$0.00" raises a question with no answer. */
                            (quote?.credit ?? 0) > 0 &&
                            <div className="plan-upgrade-row is-credit">
                                <span>Unused part of your current plan</span>
                                <span>−{money.format(quote.credit)}</span>
                            </div>
                        }
                        <div className="plan-upgrade-row is-total">
                            <span>Due today</span>
                            <span>{money.format(quote?.amountDue ?? 0)}</span>
                        </div>
                    </div>
            }

            <p className="plan-upgrade-note">
                {
                    atRenewal
                        ? `The change applies on ${renewal}. Until then your subscription page shows it, with a button to cancel it.`
                        : `Your new period starts today and renews ${period}.`
                }
            </p>

            {
                !atRenewal &&
                <>
                    <div className="plan-upgrade-section">Payment method</div>
                    <PaymentMethodPicker selectedMethodId={props.methodId} onChange={props.onMethodChange}
                        onLoadingChange={props.onLoadingMethodsChange} />
                </>
            }

            {
                /* Said before the button rather than after the surprise: pressing it leaves the
                   site. Stripe collects the card, takes the payment, and offers to keep the card
                   for next time. */
                newCard && !atRenewal &&
                <div className="plan-upgrade-note-box">
                    <Icon icon="circle-info" type="solid" className="text-primary" />
                    <div>
                        You will be taken to Stripe to enter your card and pay
                        {" "}{money.format(quote?.amountDue ?? 0)}. You can save the card there for
                        next time. Your plan changes as soon as the payment goes through.
                    </div>
                </div>
            }
        </DrawerForm>
    )
}
